import { plural } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { Text } from 'designSystem/components/Text'
import {
  TabIconSlot,
  TabTitle,
  TabTitleText,
} from 'features/knowledgeDocumentViewer/components/toolbar/documentTabsStyles'
import { useWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { getWorkspaceTabIcon } from 'features/workspaceTabs/utils/getWorkspaceTabIcon'
import { getWorkspaceTabLabel } from 'features/workspaceTabs/utils/getWorkspaceTabLabel'
import { ChevronDown, X } from 'lucide-react'
import type { MouseEvent, SyntheticEvent } from 'react'

export function WorkspaceTabSwitcher() {
  const { t } = useLingui()
  const { openTabs, activeTabId, activateWorkspaceTab, closeWorkspaceTab } =
    useWorkspaceTabs()
  const activeTab =
    openTabs.find((tab) => tab.id === activeTabId) ?? openTabs[0]
  const activeLabel = activeTab ? getWorkspaceTabLabel(activeTab) : t`Workspace`
  const ActiveIcon = activeTab ? getWorkspaceTabIcon(activeTab) : null
  const tabCount = openTabs.length
  const otherTabCount = Math.max(tabCount - 1, 0)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='sidebarRaised'
          size='lg'
          width='full'
          aria-label={plural(tabCount, {
            one: `${activeLabel}. # open tab`,
            other: `${activeLabel}. # open tabs`,
          })}>
          {ActiveIcon && (
            <TabIconSlot>
              <ActiveIcon />
            </TabIconSlot>
          )}
          <TabTitle>
            <TabTitleText>{activeLabel}</TabTitleText>
          </TabTitle>
          {otherTabCount > 0 && (
            <Text as='span' variant='caption' color='muted' wrap='nowrap'>
              <Trans>+{otherTabCount} more</Trans>
            </Text>
          )}
          <ChevronDown aria-hidden='true' color='var(--muted-foreground)' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side='bottom' align='center' width='trigger'>
        <DropdownMenuRadioGroup
          value={activeTabId}
          onValueChange={activateWorkspaceTab}>
          {openTabs.map((tab) => {
            const label = getWorkspaceTabLabel(tab)
            const Icon = getWorkspaceTabIcon(tab)
            const stopRowSelect = (event: SyntheticEvent) =>
              event.stopPropagation()
            const handleCloseClick = (event: MouseEvent<HTMLButtonElement>) => {
              stopRowSelect(event)
              closeWorkspaceTab(tab.id)
            }
            return (
              <DropdownMenuRadioItem key={tab.id} value={tab.id}>
                <Icon />
                <Box flex='one' minWidth='none'>
                  <Text as='div' variant='body' truncate>
                    {label}
                  </Text>
                </Box>
                {tab.closable !== false && (
                  <Button
                    variant='browserTabCloseAction'
                    size='icon-sm'
                    aria-label={t`Close ${label} tab`}
                    onPointerUp={stopRowSelect}
                    onClick={handleCloseClick}>
                    <X />
                  </Button>
                )}
              </DropdownMenuRadioItem>
            )
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
